import React, { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import EmailInputField from "../../components/EmailInputField";
import CTAs from "../../components/CTAs";
import Footer from "../../components/Footer";

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const API_URL = process.env.REACT_APP_API_URL;

  const handleResetPassword = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/users/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to send reset email");
      }

      alert("Reset email sent successfully");
      navigate("/resendmail", { state: { email } });
    } catch (error) {
      console.error("Error sending reset email:", error);
      alert("Error: " + error.message);
    }
  }, [email, navigate]);

  const handleResendClick = useCallback(() => {
    navigate("/resendmail", { state: { email } });
  }, [email, navigate]);

  const handleBackToLogin = useCallback(() => {
    navigate("/login");
  }, [navigate]);

  const handleEmailChange = (e) => setEmail(e.target.value);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700&display=swap');

        html, body {
          margin: 0;
          background: #ffffff !important;
        }

        .dd-root {
          font-family: 'DM Sans', sans-serif;
          background: #ffffff;
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 0 20px;
        }

        .dd-form-container {
          width: 400px;
          max-width: 100%;
          box-sizing: border-box;
          display: flex;
          flex-direction: column;
          gap: 24px;
          margin: auto;
        }

        .dd-section-label {
          font-size: 14px;
          font-weight: 700;
          color: #0F172A;
          margin-bottom: 6px;
          display: block;
        }

        .dd-required { color: #EF4444; margin-left: 2px; }

        .dd-input-group { display: flex; flex-direction: column; gap: 6px; }

        .dd-input-wrapper { position: relative; width: 100%; }

        .dd-input {
          width: 100%;
          box-sizing: border-box;
          padding: 12px 14px;
          border: 1px solid #E2E8F0;
          border-radius: 8px;
          font-family: 'DM Sans', sans-serif;
          font-size: 14px;
          color: #1E293B;
          background: #FFFFFF;
          outline: none;
          transition: border-color 0.15s;
        }
        .dd-input:focus {
          border-color: #2563EB;
          box-shadow: 0 0 0 3px rgba(37,99,235,0.1);
        }
        .dd-input.dd-input-error {
          border-color: #EF4444;
          padding-right: 36px;
        }
        .dd-input.dd-input-error:focus {
          border-color: #EF4444;
          box-shadow: 0 0 0 3px rgba(239,68,68,0.1);
        }

        .dd-error-icon {
          position: absolute;
          right: 10px;
          top: 50%;
          transform: translateY(-50%);
          color: #EF4444;
          display: flex;
        }

        .dd-error-msg {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 12px;
          color: #EF4444;
          margin-top: 4px;
        }

        /* Button styles are handled inside CTAs component */

        .dd-checkbox-label {
          display: flex; align-items: center; gap: 8px;
          font-size: 13px; color: #1E293B; cursor: pointer;
        }
        .dd-checkbox {
          width: 16px; height: 16px; accent-color: #2563EB;
        }
        .dd-link {
          font-size: 13px; font-weight: 600; color: #2563EB;
          text-decoration: none; cursor: pointer;
        }
        .dd-link:hover { text-decoration: underline; }
        .dd-text { font-size: 13px; color: #64748B; }

        .dd-icon-circle {
          width: 48px;
          height: 48px;
          border-radius: 8px;
          background: #F8FAFC;
          border: 1px solid #E2E8F0;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 8px;
        }
      `}</style>

      <div className="dd-root">
        <div className="dd-form-container">
          <div style={{ textAlign: "center" }}>
            <div className="dd-icon-circle" style={{ margin: "0 auto 16px" }}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M11.02 20.25H7.5c-.75 0-1.3-.03-1.77-.1-3.33-.37-3.98-2.35-3.98-5.65v-5c0-3.3.66-5.29 4.01-5.66.44-.06.99-.09 1.74-.09h3.46c.41 0 .75.34.75.75s-.34.75-.75.75H7.5c-.68 0-1.16.03-1.55.08-2.03.22-2.7.86-2.7 4.17v5c0 3.31.67 3.94 2.67 4.17.42.06.9.08 1.58.08h3.52c.41 0 .75.34.75.75s-.34.75-.75.75ZM16.5 20.25h-1.48c-.41 0-.75-.34-.75-.75s.34-.75.75-.75h1.48c.68 0 1.16-.03 1.55-.08 2.03-.22 2.7-.86 2.7-4.17v-5c0-3.31-.67-3.94-2.67-4.17-.42-.06-.9-.08-1.58-.08h-1.48c-.41 0-.75-.34-.75-.75s.34-.75.75-.75h1.48c.75 0 1.3.03 1.77.1 3.33.37 3.98 2.35 3.98 5.65v5c0 3.3-.66 5.29-4.01 5.66-.44.06-.99.09-1.74.09Z"
                  fill="#2d404e"
                />
                <path
                  d="M15 22.75c-.41 0-.75-.34-.75-.75V2c0-.41.34-.75.75-.75s.75.34.75.75v20c0 .41-.34.75-.75.75ZM6.7 13c-.13 0-.26-.03-.38-.08s-.23-.12-.33-.21c-.09-.1-.17-.21-.22-.33A.986.986 0 0 1 5.7 12c0-.26.11-.52.29-.71.37-.37 1.04-.37 1.42 0 .18.19.29.45.29.71 0 .13-.03.26-.08.38s-.12.23-.21.33c-.1.09-.21.16-.33.21-.12.05-.25.08-.38.08ZM10.7 13c-.13 0-.26-.03-.38-.08s-.23-.12-.33-.21c-.09-.1-.16-.21-.22-.33-.04-.12-.07-.25-.07-.38 0-.26.11-.52.29-.71.37-.37 1.05-.37 1.42 0 .18.19.29.45.29.71 0 .13-.03.26-.08.38s-.12.23-.21.33c-.1.09-.21.16-.33.21-.12.05-.25.08-.38.08Z"
                  fill="#2d404e"
                />
              </svg>
            </div>

            <h1
              style={{
                fontSize: "24px",
                fontWeight: 700,
                color: "#0F172A",
                margin: "0 0 8px 0",
              }}
            >
              Forgot password?
            </h1>
            <p className="dd-text">
              No worries, we’ll send you the reset instructions.
            </p>
          </div>

          <EmailInputField
            title="Email"
            placeholder="name@yourcompany.com"
            value={email}
            onChange={handleEmailChange}
            width="100%"
          />

          <CTAs
            type="Signinbtn"
            text="Reset password"
            width="100%"
            onClick={handleResetPassword}
          />

          <div style={{ textAlign: "center" }}>
            <span className="dd-text">Didn’t receive the email? </span>
            <span className="dd-link" onClick={handleResendClick}>
              Click to resend
            </span>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              cursor: "pointer",
            }}
            onClick={handleBackToLogin}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                stroke="#2d404e"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeMiterlimit="10"
                strokeWidth="1.5"
                d="M9.57 5.93L3.5 12l6.07 6.07M20.5 12H3.67"
              />
            </svg>
            <span className="dd-link" style={{ color: "#0F172A" }}>
              Back to login
            </span>
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
};

export default ForgotPasswordPage;
